import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CopyButton } from '../../components/CopyButton';
import { ExportButton } from '../../components/ExportButton';
import { NotFoundPage } from '../../components/NotFoundPage';
import { PageHeader } from '../../components/PageHeader';
import { usePlan } from '../../components/plan/usePlan';
import { QcStamp } from '../../components/QcStamp';
import { useQuota } from '../../components/quota/useQuota';
import { formatInstantDate, formatTimestamp } from '../../lib/format';
import { hasVisualPhase } from '../../lib/pipeline';
import { platformLabel } from '../../lib/platforms';
import { domainOf, safeExternalUrl } from '../../lib/url';
import type { GenerationRequestDetailRead, GenerationRequestStatus } from '../../types/api';
import {
  captionLengthLabel,
  captionLengthSummary,
  captionStyleLabel,
} from '../generation/captionOptions';
import { intentSummary } from '../generation/intents';
import { useCaptionOptions } from '../generation/useCaptionOptions';
import { pictureStyleLabel } from '../generation/pictureStyles';
import { usePictureStyles } from '../generation/usePictureStyles';
import { useIntents } from '../generation/useIntents';
import { useConnections } from '../knowledge-base/useConnections';
import { useBusiness } from '../onboarding/useBusiness';
import { CopySelection } from './CopySelection';
import { FinalApproval } from './FinalApproval';
import { PublishButton } from './PublishButton';
import { replacementFor } from './rejectionTrail';
import { ReviewSplit } from './ReviewSplit';
import { ShapedByLine } from './ShapedByLine';
import { SlideStrip } from './SlideStrip';
import { StageTracker } from './StageTracker';
import { useGenerationProgress } from './useGenerationProgress';
import { useGenerationRequest } from './useGenerationRequest';
import { useRecordPublished } from './useRecordPublished';
import { useRetryRequest } from './useRetryRequest';

/** Sentence-case wording for the post type. */
const POST_TYPE_LABEL: Readonly<Record<GenerationRequestDetailRead['post_type'], string>> = {
  single_image: 'Single image',
  carousel: 'Carousel',
  text_only: 'Text only',
};

/**
 * The spec sheet's "Picture style" line, or null for a run whose pictures the
 * AI does not draw (an uploaded photo, text only, a pack or a community
 * design).
 *
 * A brief that named a style reads its label. One that left it to the AI reads
 * "AI's choice", plus the style the first slide was resolved to once slides
 * exist (`custom` reads "Custom"). The first slide's `style_direction` is the
 * secondary line either way.
 */
function usePictureStyleSummary(
  request: GenerationRequestDetailRead,
): { label: string; short: string | null; direction: string | null } | null {
  const { pictureStyles } = usePictureStyles();
  if (!hasVisualPhase(request) || request.template_pack || request.community_template_id) {
    return null;
  }
  const first = request.slides.reduce<GenerationRequestDetailRead['slides'][number] | null>(
    (lowest, slide) => (lowest === null || slide.slide_index < lowest.slide_index ? slide : lowest),
    null,
  );
  // Read tolerantly: a server that predates the picture fields omits them.
  const resolved = first?.picture_style ?? null;
  const direction = first?.style_direction?.trim() || null;
  if (request.picture_style) {
    const label = pictureStyleLabel(request.picture_style, pictureStyles);
    return { label, short: label, direction };
  }
  const resolvedLabel = resolved ? pictureStyleLabel(resolved, pictureStyles) : null;
  return {
    label: resolvedLabel ? `Our pick · ${resolvedLabel}` : 'Our pick',
    // The one-line brief names the style the picture came out in, or nothing.
    short: resolvedLabel,
    direction,
  };
}

/**
 * The brief this run was made from: a closed disclosure whose summary is the
 * brief in one line ("Single image · LinkedIn · Lead attraction · Short · Flat
 * vector"), opening to the full spec sheet. The decision on this screen is the
 * picture and the caption; the brief is reference.
 */
function RequestSummary({ request }: { request: GenerationRequestDetailRead }) {
  // Provenance: the article the research node read. No row at all when the
  // brief came from nowhere but the form — an empty field is not a fact.
  const sourceHref = safeExternalUrl(request.source_url);
  // What the brief asked the post to do. Null only while the registry is in
  // flight: "Our pick" is the answer for a brief that named none, and
  // raw keys are never put in front of the user.
  const { intents } = useIntents();
  const purpose = intentSummary(request.intents, intents);
  // The caption controls, only when the brief set them (null was Auto). The
  // range is this run's platform's, from the registry; the label stands alone
  // until it arrives or when it cannot be read.
  const { options: captionOptions } = useCaptionOptions();
  const pictureStyle = usePictureStyleSummary(request);

  const oneLine = [
    POST_TYPE_LABEL[request.post_type],
    platformLabel(request.platform),
    request.intents.length > 0 ? purpose : null,
    request.caption_length ? captionLengthLabel(request.caption_length, captionOptions) : null,
    pictureStyle?.short ?? null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(' · ');

  return (
    <details className="review-brief">
      <summary className="review-brief__summary">
        <span className="review-brief__label">Brief</span>
        <span className="review-brief__line">{oneLine}</span>
      </summary>
      <dl className="spec-sheet">
        <div className="spec-sheet__row">
          <dt className="spec-sheet__term">Post type</dt>
          <dd className="spec-sheet__value">
            {POST_TYPE_LABEL[request.post_type]}
            {/* Which single image this is: one we drew, or the user's own. */}
            {request.post_type === 'single_image'
              ? request.image_asset_id === null
                ? ' · made for you'
                : ' · your photo'
              : null}
          </dd>
        </div>
        <div className="spec-sheet__row">
          <dt className="spec-sheet__term">Platform</dt>
          <dd className="spec-sheet__value">{platformLabel(request.platform)}</dd>
        </div>
        <div className="spec-sheet__row">
          <dt className="spec-sheet__term">Topic</dt>
          <dd className="spec-sheet__value">{request.topic ?? 'Picked from your brand profile'}</dd>
        </div>
        {purpose ? (
          <div className="spec-sheet__row">
            <dt className="spec-sheet__term">Purpose</dt>
            <dd className="spec-sheet__value">{purpose}</dd>
          </div>
        ) : null}
        {request.caption_length ? (
          <div className="spec-sheet__row">
            <dt className="spec-sheet__term">Length</dt>
            <dd className="spec-sheet__value">
              {captionLengthSummary(request.caption_length, request.platform, captionOptions)}
            </dd>
          </div>
        ) : null}
        {request.caption_style ? (
          <div className="spec-sheet__row">
            <dt className="spec-sheet__term">Style</dt>
            <dd className="spec-sheet__value">
              {captionStyleLabel(request.caption_style, captionOptions)}
            </dd>
          </div>
        ) : null}
        {pictureStyle ? (
          <div className="spec-sheet__row">
            <dt className="spec-sheet__term">Picture style</dt>
            <dd className="spec-sheet__value">
              <span className="spec-sheet__stack">
                <span>{pictureStyle.label}</span>
                {pictureStyle.direction ? (
                  <span className="spec-sheet__secondary">{pictureStyle.direction}</span>
                ) : null}
              </span>
            </dd>
          </div>
        ) : null}
        {request.planned_post_id ? (
          <div className="spec-sheet__row">
            <dt className="spec-sheet__term">Planned</dt>
            <dd className="spec-sheet__value">
              <Link to="/calendar">From your calendar</Link>
            </dd>
          </div>
        ) : null}
        {sourceHref ? (
          <div className="spec-sheet__row">
            <dt className="spec-sheet__term">Source</dt>
            <dd className="spec-sheet__value">
              <a href={sourceHref} target="_blank" rel="noopener noreferrer">
                {domainOf(sourceHref) ?? sourceHref}
              </a>
            </dd>
          </div>
        ) : null}
      </dl>
    </details>
  );
}

/**
 * Terminal failure. Says what happened and what to do next, without apology.
 *
 * "Try again" runs the same brief as a new request and follows it; the failed
 * run was refunded server-side, so the retry is the only run charged.
 */
function FailurePanel({
  businessId,
  request,
}: {
  businessId: string;
  request: GenerationRequestDetailRead;
}) {
  const retry = useRetryRequest(businessId, request.id);

  return (
    <section className="failure-card">
      <h2 className="failure-card__title">This post did not finish</h2>
      <p className="failure-card__reason">
        {request.failure_reason ?? 'We stopped without giving a reason.'}
      </p>
      <p className="failure-card__note">
        Nothing was published, and this didn&apos;t use a post. Try again, or create a new one.
      </p>
      <div className="page__actions">
        <button
          className="button button--primary"
          type="button"
          onClick={retry.submit}
          disabled={retry.isSubmitting}
        >
          {retry.isSubmitting ? 'Starting' : 'Try again'}
        </button>
        <Link className="button" to="/generation">
          Create a new post
        </Link>
      </div>
      {retry.error ? (
        <p className="form__error" role="alert">
          {retry.error}
        </p>
      ) : null}
    </section>
  );
}

/**
 * A draft the user rejected. Quiet on purpose: the work moved to a new request
 * and this one is just the paper trail.
 *
 * The replacement is only linked when this session is the one that rejected it —
 * v1 has no endpoint for walking a lineage, and guessing would be worse than
 * saying plainly that a new attempt exists.
 */
function RejectedPanel({ request }: { request: GenerationRequestDetailRead }) {
  const replacementId = replacementFor(request.id);

  return (
    <section className="rejected-card">
      <h2 className="rejected-card__title">Rejected</h2>
      <p className="rejected-card__note">
        Neither version went out; it stays in your history. A new try is running on your feedback.
      </p>
      <div className="page__actions">
        {replacementId ? (
          <Link className="button button--primary" to={`/review/${replacementId}`}>
            Open the new try
          </Link>
        ) : null}
        <Link className="button" to="/generation">
          Create a new post
        </Link>
      </div>
    </section>
  );
}

/**
 * The chosen copy, once selection has landed — plus whatever was rendered for it.
 *
 * This is also where the publishing story lives, and it is two stories. For a
 * LinkedIn post on a connected account there is a real Publish button: the
 * caption and its imagery go out through the API and come back with a permalink.
 * Everywhere else it stays what it always was — a zip to download and a button
 * that writes down that the user posted it, a claim rather than an observation.
 * Neither one changes the request: the caption, the slides and the review trail
 * stay exactly as they were.
 */
function ChosenCopy({
  businessId,
  request,
  onStale,
}: {
  businessId: string;
  request: GenerationRequestDetailRead;
  onStale: () => void;
}) {
  const [slideIndex, setSlideIndex] = useState(0);
  const publish = useRecordPublished(businessId, request.id, onStale);
  const { can } = usePlan();
  // Only asked for when the answer could change this screen: a run written for
  // another platform has nothing to publish to and would ignore the reply.
  const canApiPublish = request.platform === 'linkedin' || request.platform === 'facebook';
  const connections = useConnections(canApiPublish ? businessId : null);
  const connection = request.platform === 'facebook' ? connections.facebook : connections.linkedin;
  const chosen = request.variants.find((variant) => variant.id === request.selected_variant_id);
  const slides = [...request.slides].sort((left, right) => left.slide_index - right.slide_index);
  const isCarousel = request.post_type === 'carousel';
  // Whichever landed first: the record the server already had, or this session's.
  const published = request.published_post ?? publish.post;
  // A post we published ourselves is not a claim to be recorded; it is a fact
  // with a link, so "I posted this" retires and the permalink takes its place.
  const isApiPublished =
    published !== null &&
    (published.source === 'linkedin_api' || published.source === 'facebook_api');
  const permalink = safeExternalUrl(published?.external_url ?? null);
  // The publish control is the accent action when it is on screen, so the copy
  // button steps back: one filled action per screen (brand system §3).
  const offersPublish =
    canApiPublish && connection?.available === true && !published && can('publish');

  const card = (
    <section className="chosen-card">
      <div className="chosen-card__head">
        <div>
          <h2 className="chosen-card__title">
            {isCarousel
              ? 'Carousel approved'
              : slides.length > 0
                ? 'Image approved'
                : 'Copy approved'}
          </h2>
          <p className="chosen-card__lede">
            What you picked for {platformLabel(request.platform)}. LinkedIn and Facebook posts
            publish directly; the rest download for you to post.
          </p>
        </div>
        {/* One stamp per screen: it reads Posted once the run has actually shipped. */}
        {published ? (
          <div className="chosen-card__stamp">
            <QcStamp label="Posted" sub={formatInstantDate(published.posted_at)} />
            {permalink ? (
              <a
                className="chosen-card__permalink"
                href={permalink}
                target="_blank"
                rel="noopener noreferrer"
              >
                View on {platformLabel(published.platform)}
              </a>
            ) : null}
          </div>
        ) : (
          <QcStamp label="Approved" sub={formatInstantDate(request.updated_at)} />
        )}
      </div>

      {chosen ? (
        <>
          <p className="chosen-card__caption">{chosen.caption}</p>
          <ShapedByLine request={request} />
          <div className="chosen-card__actions">
            <CopyButton
              text={chosen.caption}
              label="Copy caption"
              variant={offersPublish ? 'secondary' : 'primary'}
            />
            <ExportButton
              businessId={businessId}
              requestId={request.id}
              platform={request.platform}
              label="Download"
            />
            {/* Retired the moment a record exists: the stamp and its permalink
                above are then the whole story, and a second "posted" line
                beside them would be saying it twice. */}
            {canApiPublish && published === null ? (
              <PublishButton
                businessId={businessId}
                request={request}
                connection={connection}
                onPublished={onStale}
              />
            ) : null}
            {published ? (
              isApiPublished ? null : (
                <p className="chosen-card__posted" role="status">
                  Recorded as posted on {formatInstantDate(published.posted_at)}.
                </p>
              )
            ) : (
              <button
                className="button"
                type="button"
                onClick={publish.record}
                disabled={publish.isSaving}
              >
                {publish.isSaving ? 'Saving' : 'I posted this'}
              </button>
            )}
            <Link className="button" to="/generation">
              Create another
            </Link>
          </div>
          {publish.error ? (
            <p className="form__error" role="alert">
              {publish.error}
            </p>
          ) : null}
        </>
      ) : (
        <p className="form__notice">The chosen version did not load. Reload to pull it again.</p>
      )}

      <p className="chosen-card__note">
        This post stays in your <Link to="/history">history</Link>, with the version you chose and
        the slides that went out.
      </p>
    </section>
  );

  // Whatever was rendered sits left of the card at desktop width, as approved;
  // a copy-only run is the card alone.
  return slides.length > 0 ? (
    <ReviewSplit
      picture={<SlideStrip slides={slides} currentIndex={slideIndex} onSelect={setSlideIndex} />}
    >
      {card}
    </ReviewSplit>
  ) : (
    card
  );
}

/** The header's one line, per mode: what this screen is asking of you now. */
function reviewLede(request: GenerationRequestDetailRead): string {
  switch (request.status) {
    case 'awaiting_review':
      return 'Two versions, checked and explained. You choose which one goes out. AI drafts can still be wrong.';
    case 'awaiting_final_review':
      return request.post_type === 'carousel' ? 'Approve the slides.' : 'Approve the image.';
    case 'complete':
      return 'Ready to post.';
    case 'failed':
      return 'This post did not finish.';
    case 'rejected':
      return 'Rejected. A new try took over.';
    default:
      return 'Working on it.';
  }
}

/**
 * Review screen for one generation request. One route, several modes, all driven
 * by the request's own status:
 *
 * - in flight — the live stage tracker, fed by SSE. A run that renders something
 *   (`hasVisualPhase`: a carousel, or a single image we were asked to draw)
 *   comes back here for its second phase (`designing`, `visual_guardrails`)
 *   after copy selection, and either kind comes back here for a `revising`
 *   detour when the reviewer asks for one
 * - `awaiting_review` — Review Step 1, copy selection
 * - `awaiting_final_review` — Review Step 2, the rendered deck or square
 * - `complete` — the chosen copy, plus whatever was rendered for it
 * - `failed` / `rejected` — the terminal panels
 *
 * Advisory verdicts and visual findings never block: the user always keeps
 * approve/reject control.
 */
export function ReviewPage() {
  const params = useParams<{ requestId: string }>();
  const requestId = params.requestId ?? null;
  const { businessId, isLoading: isBusinessLoading, error: businessError } = useBusiness();
  const { request, isLoading, error, notFound, refresh, applyRequest } = useGenerationRequest(
    businessId,
    requestId,
  );
  // What the run wants to tell the reviewer about work that did not land — a
  // revision the guardrails refused, say. Held on the page rather than shown as
  // a toast: it explains why the caption on screen is the one it was before,
  // and that is still true a minute later.
  const [notice, setNotice] = useState<string | null>(null);
  const { refresh: refreshQuota } = useQuota();

  // A terminal frame means the stream is done and the server has more than a
  // status for us — the variants, the slides, or the failure reason.
  const handleTerminal = useCallback(
    (status: GenerationRequestStatus, detail: string | null) => {
      // A detail on a frame that lands back at a review step is an explanation,
      // not a stage note: the run came back to where it was and the sentence
      // says why. Every other terminal frame clears it — `failed` carries its
      // reason on the request itself, and a run that landed needs no excuse.
      setNotice(
        (status === 'awaiting_review' || status === 'awaiting_final_review') && detail
          ? detail
          : null,
      );
      // A failed run is refunded server-side, so the header counter goes back
      // up by one; read it again rather than waiting for the next spend.
      if (status === 'failed') refreshQuota();
      refresh();
    },
    [refresh, refreshQuota],
  );

  const progress = useGenerationProgress(
    businessId,
    requestId,
    request?.status ?? null,
    handleTerminal,
  );

  if (requestId === null || notFound) {
    return <NotFoundPage />;
  }

  return (
    <main className="page page--review">
      <PageHeader
        section="Review"
        title="Review"
        lede={request ? reviewLede(request) : 'Loading this post.'}
        meta={request ? formatTimestamp(request.updated_at) : null}
      />

      {isBusinessLoading || isLoading ? (
        <p className="kb-section__status" role="status">
          Loading this post
        </p>
      ) : !businessId ? (
        <div className="kb-section">
          <p className="form__notice">
            {businessError ?? 'No business is linked to this account yet. Finish setup first.'}
          </p>
          <Link className="button" to="/onboarding">
            Finish setup
          </Link>
        </div>
      ) : request === null ? (
        <div className="kb-section">
          <p className="form__error" role="alert">
            {error ?? 'Could not load that post.'}
          </p>
          <div className="page__actions">
            <button className="button" type="button" onClick={refresh}>
              Try again
            </button>
            <Link className="button" to="/generation">
              Create a new post
            </Link>
          </div>
        </div>
      ) : (
        <div className="review">
          <RequestSummary request={request} />

          {error ? (
            <p className="form__error" role="alert">
              {error}
            </p>
          ) : null}

          {notice ? (
            <div className="review__notice">
              <p className="form__notice" role="status">
                {notice}
              </p>
              <button
                className="button button--ghost"
                type="button"
                onClick={() => setNotice(null)}
              >
                Dismiss
              </button>
            </div>
          ) : null}

          {request.status === 'failed' ? (
            <FailurePanel businessId={businessId} request={request} />
          ) : request.status === 'rejected' ? (
            <RejectedPanel request={request} />
          ) : request.status === 'awaiting_review' ? (
            <CopySelection
              businessId={businessId}
              request={request}
              onUpdated={applyRequest}
              onStale={refresh}
            />
          ) : request.status === 'awaiting_final_review' ? (
            <FinalApproval
              businessId={businessId}
              request={request}
              onUpdated={applyRequest}
              onStale={refresh}
            />
          ) : request.status === 'complete' ? (
            <ChosenCopy businessId={businessId} request={request} onStale={refresh} />
          ) : (
            <StageTracker
              status={progress.status}
              detail={progress.detail}
              connection={progress.connection}
              hasVisuals={hasVisualPhase(request)}
            />
          )}
        </div>
      )}
    </main>
  );
}
